<template>
  <div
    dir="rtl"
    class="flex flex-col gap-2 border border-secondary-300 rounded-md px-2">
    <PVPanel toggleable>
      <template #header>
        <div class="flex items-center gap-6 text-secondary-975">
          יצירת תמונה
        </div>
      </template>
      <template #default>
        <div class="flex flex-col gap-3 px-2">
          <div class="flex flex-col border-1 rounded-md gap-2 p-2">
            <div class="relative">
              <textarea
                ref="textareaRef"
                v-model="state.prompt"
                :rows="5"
                :disabled="isDisabled"
                placeholder="תיאור תמונה"
                class="text-dark resize-none break-all rounded-sm w-full bg-transparent outline-none overflow-y-auto px-2"
                :class="{ 'cursor-not-allowed': isDisabled }"/>
              <LoadingOverlay
                :height="20"
                :active="checked && state.isLoading"
                :opacity="0.9"
                :can-cancel="false"
                :is-full-page="false"
                class="absolute inset-0"/>
            </div>
            <div class="flex items-center gap-2 justify-start px-2">
              <button
                class="flex w-[28px] relative bg-secondary-250 h-[5px] rounded-full transition-all duration-300"
                :class="{
                  '!cursor-not-allowed !bg-secondary-450': state.isLoading,
                }"
                :disabled="state.isLoading"
                @click="checked = !checked">
                <span
                  class="circle absolute -top-[4px] left-0 flex shadow-sm rounded-full w-[13px] h-[13px] transition-all duration-300"
                  :class="{
                    'left-0 bg-secondary-900': !checked,
                    'left-4 !bg-primary-600': checked,
                    '!bg-secondary-500': state.isLoading,
                  }"
                  aria-hidden="true"></span>
              </button>
              <span class="text-xs font-simplerLight text-secondary-800">
                שיפור באמצעות AI
              </span>
            </div>
          </div>
          <div class="flex justify-center gap-2">
            <PVSelect
              v-model="state.selectedStyle"
              :options="state.styles"
              size="small"
              option-label="label"
              :disabled="isDisabled"
              class="flex-1"
              :pt="{
                trigger: {
                  class: 'w-7',
                },
                list: {
                  class: 'flex',
                },
                item: {
                  class: '!mt-0',
                },
              }"
              :pt-options="{ mergeSections: false, mergeProps: true }"
              @change="handleStyleChange">
              <template #value>
                <div class="flex items-center gap-1">
                  <Star class="h-4 stroke-secondary-800" />
                  <span class="text-xs text-secondary-800 truncate">
                    {{ selectedStyle.label }}
                  </span>
                </div>
              </template>
              <template #option="slotProps">
                <div
                  id="dropdown-content"
                  class="flex flex-col items-center justify-start gap-1">
                  <img :src="slotProps.option.img" class="w-30 h-32" />
                  <span class="text-secondary-900">
                    #{{ slotProps.option.label }}
                  </span>
                </div>
              </template>
            </PVSelect>

            <PVSelect
              v-model="state.selectedOption"
              :options="state.formats"
              size="small"
              option-label="label"
              :disabled="isDisabled"
              class="flex-1"
              :pt="{
                trigger: {
                  class: 'w-7',
                },
                list: {
                  class: 'flex-1 !p-1 z-50',
                },
                option: {
                  class: '!px-0 !py-0 text-right',
                },
              }"
              :pt-options="{ mergeSections: false, mergeProps: true }"
              @change="handleFormatChange">
              <template #value>
                <div class="flex items-center gap-2">
                  <component
                    :is="selectedFormat.icon"
                    class="w-3 stroke-secondary-800"/>
                  <span class="text-xs text-secondary-800 truncate">
                    {{ selectedFormat.label }}
                  </span>
                </div>
              </template>
              <template #option="slotProps">
                <div
                  id="dropdown-content"
                  class="flex items-center justify-start gap-1 w-full p-2"
                  :class="[
                    slotProps.option.value === state.selectedOption
                      ? 'bg-primary-600'
                      : '',
                    slotProps.option.value !== state.selectedOption
                      ? 'hover:bg-primary-100'
                      : '',
                  ]">
                  <Check
                    v-if="slotProps.option.value === state.selectedOption"
                    class="stroke-white h-3"/>
                  <component
                    :is="slotProps.option.icon"
                    class="w-3"
                    :class="
                      slotProps.option.value === state.selectedOption
                        ? 'stroke-white'
                        : 'stroke-secondary-900'
                    "/>
                  <span
                    class="font-simplerLight text-xs truncate"
                    :class="
                      slotProps.option.value === state.selectedOption
                        ? 'text-white'
                        : 'text-secondary-900'
                    ">
                    {{ slotProps.option.label }}
                  </span>
                </div>
              </template>
            </PVSelect>
          </div>
          <div class="flex flex-col gap-1">
            <BaseButton
              :color="
                state.prompt.trim().length < 1 &&
                state.formats &&
                state.selectedStyle
                  ? 'light'
                  : 'blue-light'
              "
              :disabled="isButtonDisabled"
              :loading="state.isLoading"
              class="flex items-center justify-center"
              @click="loadImage">
              יצירה
            </BaseButton>
            <span
              v-if="state.hasError"
              dir="ltr"
              class="text-center text-xs text-red-500">
              {{ state.error }}
            </span>
          </div>
        </div>
      </template>

      <template #togglericon="{ collapsed }">
        <div
          class="flex items-center justify-center h-8 w-8 bg-secondary-100 rounded-lg">
          <component
            :is="collapsed ? ChevronDown : ChevronUp"
            :size="16"
            class="text-secondary"/>
        </div>
      </template>
    </PVPanel>
    <Divider v-if="state.imageUrls.length >= 1" />
    <PVPanel v-if="hasImage || state.isLoading" toggleable>
      <template #header>
        <div class="flex items-center gap-6 text-secondary-975">
          תמונות שנוצרו
        </div>
      </template>
      <template #default>
        <div class="flex flex-col gap-2">
          <div
            v-for="(imageUrl, index) in state.imageUrls"
            :key="index"
            class="flex flex-col gap-2">
            <div class="flex flex-col gap-2">
              <div class="relative">
                <div
                  :id="`prompt-display-${index}`"
                  class="text-xs text-secondary-800 max-h-20 overflow-y-auto">
                  {{ state.imageDetails[index]?.prompt }}
                </div>
              </div>
              <div class="flex items-center justify-end gap-2">
                <div
                  class="text-xs text-secondary-800 border border-secondary-250 rounded p-1">
                  {{
                    t(
                      `blocksSettings.image_generator.${state.imageDetails[index].style}`,
                    )
                  }}
                </div>
                <div
                  class="text-xs text-secondary-800 border border-secondary-250 rounded p-1">
                  {{
                    t(
                      `blocksSettings.image_generator.${state.imageDetails[index].format}`,
                    )
                  }}
                </div>
              </div>
              <Divider />
            </div>
            <div
              class="relative flex flex-col items-center justify-center w-30 h-50 bg-secondary-500 rounded">
              <LoadingOverlay
                :height="20"
                :active="!imageUrl"
                :can-cancel="false"
                :is-full-page="false"/>
              <ImageDisplay
                v-if="imageUrl"
                :image-url="imageUrl"
                :alt="state.imageDetails[index].prompt"
                class="object-cover rounded w-full h-full"/>
            </div>
            <BaseButton
              v-if="imageUrl"
              class="flex w-fit"
              color="blue-light"
              :loading="state.isTyping[index]"
              :disabled="state.isTyping[index]"
              @click="() => useImage(imageUrl, index)">
              {{ t('blocksSettings.image_generator.edit_image') }}
              <template #suffix>
                <ArrowLeft :size="12" class="stroke-primary-600" />
              </template>
            </BaseButton>
            <Divider
              v-if="state.imageUrls.length > 1"
              style="border-top-width: 2px"/>
          </div>
        </div>
      </template>
      <template #togglericon="{ collapsed }">
        <div
          class="flex items-center justify-center h-8 w-8 bg-secondary-100 rounded-lg">
          <component
            :is="collapsed ? ChevronDown : ChevronUp"
            :size="16"
            class="text-secondary"/>
        </div>
      </template>
    </PVPanel>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch, type Component } from 'vue';
import { useI18n } from 'vue-i18n';
import ImageDisplay from './ImageDisplay.vue';
import LoadingOverlay from 'vue-loading-overlay';
import vivid from '../../../assets/vivid.png';
import natural from '../../../assets/natural.png';
import { BaseButton } from '@nwire/amit-design-system';
import { generateImage } from '../../../services/openai.ts';
import { reactiveUploadResult, updateAndOpenInstance } from '@amit/composables';
import PVPanel from 'primevue/panel';
import PVSelect from 'primevue/select';
import {
  RectangleVertical,
  RectangleHorizontal,
  ChevronUp,
  ChevronDown,
  Square,
  Star,
  ArrowLeft,
  Check,
} from 'lucide-vue-next';
import Divider from 'primevue/divider';

interface ImageDetail {
  prompt: string;
  style: string;
  format: string;
}

interface Format {
  label: string;
  value: string;
  icon: Component;
}

interface ImageStyle {
  label: string;
  value: string;
  img: string;
}

const { t } = useI18n();
const MAX_PROMPT_LENGTH = 1000;

const FORMATS: Format[] = [
  { label: '1:1', value: 'square', icon: Square },
  { label: '16:9', value: 'landscape', icon: RectangleHorizontal },
  { label: '9:16', value: 'portrait', icon: RectangleVertical },
];

const STYLES: ImageStyle[] = [
  { label: 'סגנון טבעי', value: 'natural', img: natural },
  { label: 'סגנון היפר ראליסטי', value: 'vivid', img: vivid },
];

const props = defineProps<{
  id: string;
}>();

const imageSrc = defineModel<string>('src', { required: true });
const imageAlt = defineModel<string>('alt', { required: false });

const checked = ref(false);
const textareaRef = ref<HTMLTextAreaElement | null>(null);

const state = reactive({
  formats: FORMATS,
  styles: STYLES,
  selectedOption: 'square',
  selectedStyle: 'natural',
  prompt: '',
  imageUrls: [] as string[],
  imageDetails: [] as ImageDetail[],
  isLoading: false,
  isTyping: {} as Record<number, boolean>,
  hasError: false,
  error: {},
  rules: {
    prompt: [
      (v: string | any[]) =>
        v.length <= MAX_PROMPT_LENGTH ||
        t('home_view.max_chars', { max: MAX_PROMPT_LENGTH }),
    ],
  },
});
const selectedFormat = ref(state.formats[0]);
const selectedStyle = ref(state.styles[0]);

const handleStyleChange = event => {
  selectedStyle.value = event.value;
  state.selectedStyle = event.value.value;
};

const isDisabled = computed(() => {
  return state.isLoading || Object.values(state.isTyping).some(Boolean);
});

const isButtonDisabled = computed(() => {
  return (
    isDisabled.value ||
    (state.prompt.trim().length < 1 && state.formats && state.selectedStyle)
  );
});

watch(
  () => state.prompt,
  async () => {
    if (textareaRef.value) {
      await scrollToBottom(textareaRef.value);
    }
  },
);

const handleFormatChange = event => {
  selectedFormat.value = event.value;
  state.selectedOption = event.value.value;
};

const useImage = (source, index) => {
  updateAndOpenInstance({}, props.id, [source]);
  imageAlt.value = state.imageDetails[index].prompt;
};

watch(
  () => reactiveUploadResult.value,
  () => {
    if (
      reactiveUploadResult.value.block === props.id &&
      reactiveUploadResult.value.type === 'success'
    ) {
      imageSrc.value = reactiveUploadResult.value.result;
    }
  },
  { deep: true },
);

const hasImage = computed(() => {
  return state.imageUrls.length > 0;
});

const scrollToBottom = async (element: HTMLElement) => {
  await nextTick();
  element.scrollTop = element.scrollHeight;
};

const typeWriterEffect = (
  text: string,
  index: number,
  callback: (char: string) => void,
) => {
  let i = 0;
  const speed = 40;
  state.isTyping[index] = true;

  function typeWriter() {
    if (i < text.length) {
      callback(text.charAt(i));
      i++;
      const promptDiv = document.querySelector(`#prompt-display-${index}`);
      if (promptDiv) {
        scrollToBottom(promptDiv as HTMLElement);
      }
      setTimeout(typeWriter, speed);
    } else {
      state.isTyping[index] = false;
    }
  }

  typeWriter();
};

// https://platform.openai.com/docs/guides/images/usage
const loadImage = async () => {
  state.isLoading = true;
  state.hasError = false;
  state.error = {};
  state.imageUrls.unshift('');
  const originalPrompt = state.prompt;
  if (checked.value) {
    state.prompt = '';
  }
  state.imageDetails.unshift({
    prompt: state.prompt,
    style: state.selectedStyle,
    format: state.selectedOption,
  });

  const { onSuccess, onError } = generateImage({
    prompt: originalPrompt,
    size: state.selectedOption,
    style: state.selectedStyle,
  });

  onError(error => {
    state.isLoading = false;
    state.hasError = true;
    state.error = 'יצירת תמונה נכשלה';
    // t('blocksSettings.image_generator.error_message') || error.message;
    state.imageUrls.shift();
  });

  onSuccess(({ data }: { data: { revised_prompt?: string; url: string } }) => {
    const { revised_prompt, url } = data;
    state.isLoading = false;
    state.hasError = false;
    state.imageUrls[0] = url;

    const finalPrompt = checked.value ? revised_prompt || '' : originalPrompt;
    state.prompt = '';

    typeWriterEffect(finalPrompt, 0, char => {
      state.prompt += char;
      state.imageDetails[0].prompt = state.prompt;
    });
  });
};
</script>

<style scoped>
.image-display > .spinner-wrapper {
  margin: 0 auto;
}
</style>
